// Gemeinsame Fiat-UI-Hilfen: Backend-Währung first, Legacy-EUR-Fallbacks

export function firstFinite(...vals) {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

export function fallbackCurrencyFromCountry(selectedCountry = "DE") {
  const cc = String(selectedCountry || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (cc === "US" || cc === "USA" || cc.includes("UNITED_STATES")) return "USD";
  if (cc === "UK" || cc === "GB" || cc.includes("UNITED_KINGDOM")) return "GBP";
  if (cc === "CH" || cc === "CHE" || cc.includes("SWITZERLAND")) return "CHF";
  return "EUR";
}

function hasFiatPayload(src = {}) {
  const moneyFields = [
    src?.disposalNetFiat, src?.disposalNetEUR,
    src?.disposalGainFiat, src?.disposalGainEUR,
    src?.disposalLossFiat, src?.disposalLossEUR,
    src?.otherIncomeFiat, src?.otherIncomeEUR,
    src?.incomeFiat, src?.incomeEUR,
    src?.totalTaxableFiat, src?.totalTaxableEUR,
    src?.disposalShortTermNetFiat, src?.disposalShortTermNetEUR,
    src?.disposalLongTermNetFiat, src?.disposalLongTermNetEUR,

    src?.unitPriceFiat, src?.priceFiat, src?.fiatPrice,
    src?.amountFiat, src?.totalFiat, src?.fiatAmount,
    src?.grossBuyFiat, src?.grossBuyEUR,
    src?.grossSellFiat, src?.grossSellEUR,
    src?.grossProfitFiat, src?.grossProfitEUR,
    src?.taxableProfitFiat, src?.taxableProfitEUR, src?.taxableProfit,
    src?.nonTaxableProfitFiat, src?.nonTaxableProfitEUR,

    src?.valuationPrice,
    src?.unit_cost_fiat,
    src?.buyFiat,
    src?.sellFiat,
    src?.legProfitFiat,
  ];

  return moneyFields.some(v => v !== null && v !== undefined && v !== "");
}

export function currencyFromBackend(selectedCountry = "DE", ...sources) {
  const normalizedSources = sources.filter(Boolean);

  for (const src of normalizedSources) {
    const c = String(
      src?.currency || src?.fiatCurrency || src?.priceCurrency || ""
    ).toUpperCase();
    if (c === "USD" || c === "EUR" || c === "GBP" || c === "CHF") return c;
  }

  const hasMeaningfulFiatSource = normalizedSources.some((src) => hasFiatPayload(src));
  if (hasMeaningfulFiatSource) {
    return fallbackCurrencyFromCountry(selectedCountry);
  }

  // Für leere Placeholder / Preview / UI-only Zustände weiterhin fallbacken
  return fallbackCurrencyFromCountry(selectedCountry);
}

export function currencySymbol(currency = "EUR") {
  const c = String(currency).toUpperCase();
  if (c === "USD") return "$";
  if (c === "EUR") return "€";
  if (c === "GBP") return "£";
  if (c === "CHF") return "CHF";
  return "¤";
}

export function totalsView(totals = {}, selectedCountry = "DE") {
  const currency = currencyFromBackend(selectedCountry, totals);
  return {
    currency,
    swapProfit: firstFinite(
      totals.disposalNetFiat,
      totals.disposalNetEUR,
      totals.disposalEUR,
      totals.disposalProfitEUR
    ),
    swapGain: firstFinite(totals.disposalGainFiat, totals.disposalGainEUR),
    swapLoss: firstFinite(totals.disposalLossFiat, totals.disposalLossEUR),
    otherIncome: firstFinite(
      totals.otherIncomeFiat,
      totals.otherIncomeEUR,
      totals.incomeFiat,
      totals.incomeEUR
    ),
    totalTaxable: firstFinite(totals.totalTaxableFiat, totals.totalTaxableEUR),
    shortTerm: firstFinite(
      totals.disposalShortTermNetFiat,
      totals.disposalShortTermNetEUR
    ),
    longTerm: firstFinite(
      totals.disposalLongTermNetFiat,
      totals.disposalLongTermNetEUR
    ),
  };
}

export function rowUnitPrice(row = {}) {
  return firstFinite(
    row.unitPriceFiat,
    row.priceFiat,
    row.fiatPrice,
    row.valuationPrice,
    row.euroPrice,
    row.unitEur,
    row.sellUnitPriceEur,
    row.sell_unit_price_eur,
    row.priceEUR,
    row.price_eur,
    row.unit_cost_fiat,
    row.unit_cost_eur
  );
}

export function rowTotal(row = {}) {
  return firstFinite(
    row.amountFiat,
    row.totalFiat,
    row.fiatAmount,
    row.grossSellFiat,
    row.proceedsFiat,
    row.euroAmount,
    row.grossSellEUR,
    row.lpDisposalEuroAmount,
    row.valueEUR,
    row.value_eur
  );
}

export function rowBuy(row = {}) {
  return firstFinite(
    row.grossBuyFiat,
    row.buyFiat,
    row.costBasisFiat,
    row.grossBuyEUR,
    row.buyEUR
  );
}

export function rowSell(row = {}) {
  return firstFinite(
    row.grossSellFiat,
    row.sellFiat,
    row.proceedsFiat,
    row.grossSellEUR,
    row.sellEUR,
    row.euroAmount
  );
}

export function rowProfit(row = {}) {
  return firstFinite(
    row.grossProfitFiat,
    row.profitFiat,
    row.grossProfitEUR,
    row.profitEUR,
    row.legProfitFiat,
    row.legProfitEUR
  );
}

export function rowTaxable(row = {}) {
  return firstFinite(
    row.taxableProfitFiat,
    row.taxableFiat,
    row.taxableProfitEUR,
    row.taxableProfit,
    row.fifoProfitEUR
  );
}

export function rowNonTaxable(row = {}) {
  return firstFinite(
    row.nonTaxableProfitFiat,
    row.nonTaxableFiat,
    row.nonTaxableProfitEUR,
    row.nonTaxableProfit
  );
}
